import { useEffect, useMemo, useRef, useState } from 'react';
import { LuCheck, LuChevronRight, LuFileSpreadsheet } from 'react-icons/lu';
import RecordPanel, { PanelSection } from './RecordPanel';
import Select, { type SelectOption } from './Select';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    DESTINOS, lerPlanilha, montarLinhas, sugerirColunas, temIdentidade,
    type Contexto, type Destino, type Planilha, type Sugestao,
} from '../lib/planilha';
import {
    ImportacaoService, LIMITE_LINHAS, type AvisoImportacao, type CodigoAviso, type ResultadoImportacao,
} from '../services/ImportacaoService';
import './ImportarPlanilhaPanel.css';

/* ─────────────────────────────────────────────────────────────
   Importar planilha — Contatos e Organizações (04/10/2026).

   O mesmo painel lateral dos cadastros, em três passos: arquivo →
   colunas → prévia. A prévia é a API rodando o plano inteiro sem
   gravar (`simular`), então os números que a tela mostra são os que
   a gravação produz. Só o administrador chega aqui (a API também
   recusa os outros).
   ───────────────────────────────────────────────────────────── */

type Passo = 'arquivo' | 'colunas' | 'previa' | 'feito';

const PASSOS: { id: Exclude<Passo, 'feito'>; rotulo: string }[] = [
    { id: 'arquivo', rotulo: 'Arquivo' },
    { id: 'colunas', rotulo: 'Colunas' },
    { id: 'previa', rotulo: 'Prévia' },
];

const TITULO_AVISO: Record<CodigoAviso, string> = {
    'sem-contato': 'Sem telefone nem e-mail',
    'telefone-invalido': 'Telefone que não dá para usar',
    'email-invalido': 'E-mail inválido',
    'sem-cnpj': 'Empresa sem CNPJ',
    'cnpj-invalido': 'CNPJ inválido',
    'cnpj-sem-nome': 'CNPJ sem razão social',
    'telefone-excluido': 'Telefone de contato excluído',
    'outra-organizacao': 'Já está em outra organização',
    'linha-vazia': 'Linha sem dados',
};

const OPCOES: SelectOption[] = [
    { value: '', label: 'Ignorar' },
    ...DESTINOS.map(d => ({ value: d.value, label: d.label, group: d.grupo })),
];

const formatar = (n: number) => n.toLocaleString('pt-BR');
const plural = (n: number, um: string, varios: string) => `${formatar(n)} ${n === 1 ? um : varios}`;

interface Props {
    open: boolean;
    onClose: () => void;
    /** De qual tela abriu: decide para que lado vão e-mail, telefone e endereço por padrão. */
    contexto: Contexto;
    /** Depois de gravar, para a lista de trás recarregar. */
    onConcluido: () => void;
}

const ImportarPlanilhaPanel = ({ open, onClose, contexto, onConcluido }: Props) => {
    const [passo, setPasso] = useState<Passo>('arquivo');
    const [planilha, setPlanilha] = useState<Planilha | null>(null);
    const [destinos, setDestinos] = useState<Destino[]>([]);
    const [notas, setNotas] = useState<(Pick<Sugestao, 'nota' | 'alerta'> | undefined)[]>([]);
    const [erroArquivo, setErroArquivo] = useState<string | null>(null);
    const [arrastando, setArrastando] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [resultado, setResultado] = useState<ResultadoImportacao | null>(null);
    const entrada = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setPasso('arquivo');
        setPlanilha(null);
        setDestinos([]);
        setNotas([]);
        setErroArquivo(null);
        setResultado(null);
    }, [open]);

    const ler = async (arquivo: File | undefined) => {
        if (!arquivo) return;
        setErroArquivo(null);
        setOcupado(true);
        try {
            const lida = await lerPlanilha(arquivo);
            if (lida.linhas.length > LIMITE_LINHAS) {
                setErroArquivo(`A planilha tem ${formatar(lida.linhas.length)} linhas e o limite é ${formatar(LIMITE_LINHAS)} por importação. Divida o arquivo e importe em partes.`);
                return;
            }
            const sugestoes = sugerirColunas(lida, contexto);
            setPlanilha(lida);
            setDestinos(sugestoes.map(s => s.destino));
            setNotas(sugestoes.map(s => (s.nota ? { nota: s.nota, alerta: s.alerta } : undefined)));
            setPasso('colunas');
        } catch (err) {
            setErroArquivo(textoDoErro(err, 'Não foi possível ler o arquivo. Confira se é um .csv ou .xlsx.'));
        } finally {
            setOcupado(false);
            if (entrada.current) entrada.current.value = '';
        }
    };

    /* Um campo, uma coluna: escolher um destino já usado tira ele da outra coluna. */
    const escolher = (coluna: number, destino: Destino) => {
        setDestinos(atuais => atuais.map((d, i) => (i === coluna ? destino : destino !== '' && d === destino ? '' : d)));
        setNotas(atuais => atuais.map((n, i) => (i === coluna || (destino !== '' && destinos[i] === destino) ? undefined : n)));
    };

    const enviar = async (simular: boolean) => {
        if (!planilha) return;
        setOcupado(true);
        try {
            const res = await ImportacaoService.importar(montarLinhas(planilha, destinos), simular);
            setResultado(res);
            setPasso(simular ? 'previa' : 'feito');
            if (!simular) onConcluido();
        } catch (err) {
            aviso.erro(textoDoErro(err, simular ? 'Não foi possível montar a prévia.' : 'Não foi possível importar a planilha.'));
        } finally {
            setOcupado(false);
        }
    };

    const exemplos = useMemo(() => planilha?.cabecalho.map((_, coluna) =>
        planilha.linhas.map(l => l[coluna]).filter(v => v !== '').slice(0, 2)) ?? [], [planilha]);

    const podeVerPrevia = temIdentidade(destinos);
    const algoParaGravar = !!resultado && (resultado.contatosNovos + resultado.contatosCompletados
        + resultado.organizacoesNovas + resultado.organizacoesCompletadas) > 0;

    const indice = passo === 'feito' ? PASSOS.length : PASSOS.findIndex(p => p.id === passo);
    const subtitulo = passo === 'feito'
        ? 'Importação concluída'
        : `Passo ${indice + 1} de ${PASSOS.length} · ${PASSOS[indice].rotulo}`;

    const rodape = (() => {
        if (passo === 'arquivo') {
            return <button type="button" className="rp-btn" onClick={onClose}>Cancelar</button>;
        }
        if (passo === 'colunas') {
            return (
                <>
                    <button type="button" className="rp-btn" onClick={() => setPasso('arquivo')} disabled={ocupado}>Trocar arquivo</button>
                    <button type="button" className="rp-btn is-primary ip-avancar" onClick={() => enviar(true)} disabled={ocupado || !podeVerPrevia}>
                        {ocupado ? 'Montando a prévia…' : 'Ver prévia'}
                    </button>
                </>
            );
        }
        if (passo === 'previa') {
            return (
                <>
                    <button type="button" className="rp-btn" onClick={() => setPasso('colunas')} disabled={ocupado}>Voltar às colunas</button>
                    <button type="button" className="rp-btn is-primary ip-avancar" onClick={() => enviar(false)} disabled={ocupado || !algoParaGravar}>
                        {ocupado ? 'Importando…' : 'Importar'}
                    </button>
                </>
            );
        }
        return <button type="button" className="rp-btn is-primary ip-avancar" onClick={onClose}>Fechar</button>;
    })();

    return (
        <RecordPanel
            open={open}
            onClose={onClose}
            title="Importar planilha"
            subtitle={subtitulo}
            wide
            persistentFooter
            footer={rodape}
        >
            <ol className="ip-steps" aria-label="Passos da importação">
                {PASSOS.map((p, i) => (
                    <li key={p.id} className={i < indice ? 'is-done' : i === indice ? 'is-current' : ''}
                        aria-current={i === indice ? 'step' : undefined}>
                        <span className="ip-step-n">{i < indice ? <LuCheck size={11} /> : i + 1}</span>
                        {p.rotulo}
                        {i < PASSOS.length - 1 && <LuChevronRight size={13} className="ip-step-sep" aria-hidden="true" />}
                    </li>
                ))}
            </ol>

            {passo === 'arquivo' && (
                <>
                    <PanelSection title="Arquivo">
                        <label
                            className={`ip-drop ${arrastando ? 'is-over' : ''} ${ocupado ? 'is-busy' : ''}`}
                            onDragOver={e => { e.preventDefault(); setArrastando(true); }}
                            onDragLeave={() => setArrastando(false)}
                            onDrop={e => { e.preventDefault(); setArrastando(false); ler(e.dataTransfer.files[0]); }}
                        >
                            <LuFileSpreadsheet size={24} className="ip-drop-ico" aria-hidden="true" />
                            <span className="ip-drop-title">{ocupado ? 'Lendo a planilha…' : 'Arraste a planilha para cá'}</span>
                            <span className="ip-drop-or">ou</span>
                            <span className="rp-btn">Escolher arquivo</span>
                            <input
                                ref={entrada}
                                type="file"
                                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                className="ip-file"
                                disabled={ocupado}
                                onChange={e => ler(e.target.files?.[0])}
                            />
                        </label>
                        {erroArquivo
                            ? <p className="rp-error">{erroArquivo}</p>
                            : <p className="rp-hint">CSV ou Excel (.xlsx), com o cabeçalho na primeira linha. Até {formatar(LIMITE_LINHAS)} linhas.</p>}
                    </PanelSection>

                    <PanelSection title="Como funciona">
                        <ul className="ip-rules">
                            <li>Você confere para onde vai cada coluna antes de qualquer coisa ser gravada.</li>
                            <li>Quem já existe — mesmo telefone ou e-mail — só ganha o que estiver vazio no cadastro.</li>
                            <li>Organização só é criada quando a linha tem CNPJ válido.</li>
                            <li>A importação só cadastra: nenhuma oportunidade é aberta.</li>
                        </ul>
                    </PanelSection>
                </>
            )}

            {passo === 'colunas' && planilha && (
                <PanelSection
                    title="Para onde vai cada coluna"
                    extra={<span className="ip-meta">{planilha.arquivo} · {plural(planilha.linhas.length, 'linha', 'linhas')}</span>}
                >
                    <div className="ip-map" role="table" aria-label="Colunas da planilha">
                        <div className="ip-map-head" role="row">
                            <span role="columnheader">Coluna da planilha</span>
                            <span role="columnheader">Exemplo</span>
                            <span role="columnheader">Vai para</span>
                        </div>
                        {planilha.cabecalho.map((titulo, coluna) => (
                            <div key={coluna} className={`ip-map-row ${destinos[coluna] ? '' : 'is-off'}`} role="row">
                                <span className="ip-col" role="cell" title={titulo}>{titulo}</span>
                                <span className="ip-ex" role="cell" title={exemplos[coluna]?.join(' · ')}>
                                    {exemplos[coluna]?.length ? exemplos[coluna].join(' · ') : <em>vazia</em>}
                                </span>
                                <span role="cell">
                                    <Select
                                        size="sm"
                                        portal
                                        options={OPCOES}
                                        value={destinos[coluna] ?? ''}
                                        onChange={v => escolher(coluna, v as Destino)}
                                        ariaLabel={`Destino da coluna ${titulo}`}
                                    />
                                </span>
                                {notas[coluna] && (
                                    <span className={`ip-note ${notas[coluna]?.alerta ? 'is-alert' : ''}`} role="note">{notas[coluna]?.nota}</span>
                                )}
                            </div>
                        ))}
                    </div>
                    {!podeVerPrevia && (
                        <p className="rp-hint ip-missing">
                            Escolha a coluna de telefone, de e-mail ou de CNPJ: sem uma delas não há como saber quem já está cadastrado.
                        </p>
                    )}
                </PanelSection>
            )}

            {(passo === 'previa' || passo === 'feito') && resultado && (
                <>
                    <PanelSection title={passo === 'previa' ? 'O que vai acontecer' : 'O que foi feito'}>
                        <Numeros resultado={resultado} feito={passo === 'feito'} />
                        {passo === 'previa' && !algoParaGravar && (
                            <p className="rp-hint">Nada desta planilha muda o cadastro. Confira as colunas ou os avisos abaixo.</p>
                        )}
                    </PanelSection>

                    {resultado.avisos.length > 0 && (
                        <PanelSection title="Avisos">
                            <Avisos avisos={resultado.avisos} />
                        </PanelSection>
                    )}
                </>
            )}
        </RecordPanel>
    );
};

const Numeros = ({ resultado: r, feito }: { resultado: ResultadoImportacao; feito: boolean }) => {
    /* [um, vários]: "1 contato novo", "2 contatos novos". */
    type Item = { n: number; texto: [string, string]; tom?: 'good' | 'muted' };
    const todos: Item[] = [
        { n: r.contatosNovos, tom: 'good', texto: feito ? ['contato criado', 'contatos criados'] : ['contato novo', 'contatos novos'] },
        { n: r.contatosCompletados, texto: feito
            ? ['contato que já existia foi completado', 'contatos que já existiam foram completados']
            : ['contato já existe e ganha o que falta', 'contatos já existem e ganham o que falta'] },
        { n: r.contatosSemMudanca, tom: 'muted', texto: ['contato já existe e a planilha não traz nada novo', 'contatos já existem e a planilha não traz nada novo'] },
        { n: r.organizacoesNovas, tom: 'good', texto: feito ? ['organização criada', 'organizações criadas'] : ['organização nova', 'organizações novas'] },
        { n: r.organizacoesCompletadas, texto: feito
            ? ['organização que já existia foi completada', 'organizações que já existiam foram completadas']
            : ['organização já existe e ganha o que falta', 'organizações já existem e ganham o que falta'] },
        { n: r.organizacoesSemMudanca, tom: 'muted', texto: ['organização já existe e a planilha não traz nada novo', 'organizações já existem e a planilha não traz nada novo'] },
        { n: r.vinculos, texto: feito
            ? ['contato ligado a uma organização', 'contatos ligados a uma organização']
            : ['contato será ligado a uma organização', 'contatos serão ligados a uma organização'] },
        { n: r.linhasIgnoradas, tom: 'muted', texto: ['linha ignorada (veja os avisos)', 'linhas ignoradas (veja os avisos)'] },
    ];
    const itens = todos.filter(i => i.n > 0);

    return (
        <ul className="ip-nums">
            <li className="ip-num is-total">
                <strong>{formatar(r.totalLinhas)}</strong>
                <span>{r.totalLinhas === 1 ? 'linha lida' : 'linhas lidas'}</span>
            </li>
            {itens.map(i => (
                <li key={i.texto[1]} className={`ip-num ${i.tom ? `is-${i.tom}` : ''}`}>
                    <strong>{formatar(i.n)}</strong>
                    <span>{i.n === 1 ? i.texto[0] : i.texto[1]}</span>
                </li>
            ))}
        </ul>
    );
};

/* Agrupados pelo motivo: 40 linhas com o mesmo problema são um aviso, não 40. */
const Avisos = ({ avisos }: { avisos: AvisoImportacao[] }) => {
    const grupos = useMemo(() => {
        const mapa = new Map<CodigoAviso, AvisoImportacao[]>();
        avisos.forEach(a => mapa.set(a.codigo, [...(mapa.get(a.codigo) ?? []), a]));
        return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length);
    }, [avisos]);

    return (
        <div className="ip-warns">
            {grupos.map(([codigo, lista]) => (
                <details key={codigo} className="ip-warn">
                    <summary>
                        <LuChevronRight size={13} className="ip-warn-chev" aria-hidden="true" />
                        <span className="ip-warn-title">{TITULO_AVISO[codigo] ?? codigo}</span>
                        <span className="ip-warn-count">{plural(lista.length, 'linha', 'linhas')}</span>
                    </summary>
                    <ul>
                        {lista.slice(0, 100).map((a, i) => (
                            <li key={`${a.linha}-${i}`}>
                                <span className="ip-warn-line">Linha {a.linha}</span>
                                <span>{a.mensagem}</span>
                            </li>
                        ))}
                        {lista.length > 100 && <li className="ip-warn-more">e mais {formatar(lista.length - 100)}.</li>}
                    </ul>
                </details>
            ))}
        </div>
    );
};

export default ImportarPlanilhaPanel;
